'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { MapPin, Clock, ArrowRight, Package } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

const conditionMap: Record<string, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'badge-new' },
  'like-new': { label: 'Like New', cls: 'badge-like-new' },
  used: { label: 'Used', cls: 'badge-used' },
  'well-used': { label: 'Well Used', cls: 'badge-well-used' },
};

type Listing = {
  id: string;
  title: string;
  price: number;
  category: string;
  condition: string;
  location: string;
  created_at: string;
  images: { src: string; alt: string }[];
};

function ConditionBadge({ condition }: { condition: string }) {
  const meta = conditionMap[condition] ?? { label: condition, cls: 'badge-used' };
  return <span className={meta.cls}>{meta.label}</span>;
}

export default function FeaturedListings() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('listings')
      .select('id, title, price, category, condition, location, created_at, images')
      .eq('listing_status', 'active')
      .order('created_at', { ascending: false })
      .limit(8)
      .then(({ data }) => {
        setListings((data as unknown as Listing[]) || []);
        setLoading(false);
      });
  }, []);

  if (loading) return null;

  if (listings.length === 0) {
    return (
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-4 pb-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Just Listed</p>
            <h2 className="text-2xl font-bold text-white">Recent Listings</h2>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-[hsl(222,25%,18%)] rounded-2xl">
          <div className="w-12 h-12 rounded-2xl bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] flex items-center justify-center mb-4">
            <Package className="w-5 h-5 text-slate-500" />
          </div>
          <h3 className="text-base font-semibold text-white mb-2">No listings live yet</h3>
          <p className="text-sm text-slate-500 mb-5 max-w-xs">Be the first to list your working dog equipment on DrivenMarket.</p>
          <Link href="/create-listing" className="flex items-center gap-2 bg-[hsl(28,85%,48%)] hover:bg-[hsl(28,85%,41%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150 active:scale-95">
            Create Listing
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-4 pb-14">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Just Listed</p>
          <h2 className="text-2xl font-bold text-white">Recent Listings</h2>
        </div>
        <Link href="/listings-feed" className="hidden sm:flex items-center gap-1.5 text-sm text-slate-400 hover:text-[hsl(28,85%,58%)] transition-colors duration-150">
          Browse all
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {listings.map((listing) => {
          const firstImage = listing.images?.[0];
          const timeAgo = (() => {
            const diff = Date.now() - new Date(listing.created_at).getTime();
            const h = Math.floor(diff / 3600000);
            if (h < 1) return 'Just now';
            if (h < 24) return `${h}h ago`;
            return `${Math.floor(h / 24)}d ago`;
          })();
          return (
            <Link key={listing.id} href={`/listing-detail?id=${listing.id}`} className="listing-card group">
              <div className="relative aspect-[4/3] overflow-hidden bg-[hsl(222,35%,10%)]">
                {firstImage ? (
                  <AppImage src={firstImage.src} alt={firstImage.alt || listing.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" className="object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-slate-600" /></div>
                )}
                <div className="absolute top-2 right-2">
                  <ConditionBadge condition={listing.condition} />
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-[hsl(28,85%,58%)] transition-colors duration-150 mb-2">{listing.title}</h3>
                <div className="text-xl font-bold text-white tabular-nums mb-3">£{listing.price?.toLocaleString()}</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <MapPin className="w-3 h-3" />
                    {listing.location}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-600">
                    <Clock className="w-3 h-3" />
                    {timeAgo}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[hsl(222,25%,18%)]">
                  <span className="text-xs text-slate-600 bg-white/5 px-2 py-0.5 rounded-md">{listing.category}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="sm:hidden mt-6">
        <Link href="/listings-feed" className="flex items-center justify-center gap-2 w-full border border-[hsl(222,25%,18%)] text-slate-300 hover:text-white hover:border-slate-500 py-3 rounded-xl text-sm font-medium transition-all duration-150">
          Browse all listings
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}